import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { AutoSaveNote } from "./AutoSaveNote";
import { MapPin, Trash2, ChevronDown, ChevronUp, StickyNote, Calendar, Clock, FileText, Pencil, Download, Plus, X, ChevronLeft, ArrowRight, BookUser, Keyboard } from "lucide-react";

function EditableNote({ value, onSave }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="mt-1.5">
        <AutoSaveNote value={value} onSave={onSave} placeholder="Add a note..." />
      </div>
    );
  }

  if (value) {
    return (
      <div
        onClick={() => setEditing(true)}
        className="text-xs text-slate-600 bg-amber-50 p-1.5 rounded border border-amber-100 mt-1.5 cursor-pointer hover:bg-amber-100 transition-colors group"
      >
        <StickyNote className="w-3 h-3 inline mr-1" />
        {value}
        <Pencil className="w-3 h-3 inline ml-1.5 opacity-0 group-hover:opacity-100 text-slate-400 transition-opacity" />
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="text-xs text-slate-400 hover:text-slate-600 mt-1 flex items-center gap-1 transition-colors"
    >
      <Pencil className="w-3 h-3" />Add note
    </button>
  );
}

function ContactPicker({ contacts, onAdd, onCancel }) {
  const [mode, setMode] = useState("contacts"); // "contacts" | "manual"
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [manualName, setManualName] = useState("");
  const [manualPostcode, setManualPostcode] = useState("");

  const alphabet = useMemo(() => {
    const letters = new Set((contacts || []).map(c => c.name.charAt(0).toUpperCase()));
    return Array.from(letters).sort();
  }, [contacts]);

  const filteredNames = useMemo(() => {
    if (!selectedLetter || !contacts) return [];
    const names = [...new Set(
      contacts.filter(c => c.name.charAt(0).toUpperCase() === selectedLetter).map(c => c.name)
    )];
    return names.sort();
  }, [contacts, selectedLetter]);

  const postcodesForName = useMemo(() => {
    if (!selectedName || !contacts) return [];
    const pcs = [];
    contacts.filter(c => c.name === selectedName).forEach(c => {
      const arr = c.postcodes?.length ? c.postcodes : (c.postcode ? [c.postcode] : []);
      arr.forEach(pc => {
        if (pc && !pcs.some(p => p.toUpperCase() === pc.toUpperCase())) pcs.push(pc);
      });
    });
    return pcs;
  }, [contacts, selectedName]);

  const handleBack = () => {
    if (selectedName) setSelectedName(null);
    else if (selectedLetter) setSelectedLetter(null);
  };

  const handleManualSubmit = () => {
    if (!manualName.trim() || !manualPostcode.trim()) return;
    onAdd({ name: manualName.trim(), postcode: manualPostcode.trim() });
  };

  const fullAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Contact flow — drilling into letter → name → postcode
  if (mode === "contacts" && selectedName) {
    return (
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleBack} className="h-7 w-7">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <p className="text-sm font-semibold text-slate-900">{selectedName}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} className="h-7 w-7">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="max-h-[200px] overflow-y-auto space-y-1.5">
          {postcodesForName.map((pc) => (
            <button
              key={pc}
              onClick={() => onAdd({ name: selectedName, postcode: pc })}
              className="w-full p-2.5 rounded-lg border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-left transition-all flex items-center justify-between text-sm"
            >
              <span className="font-mono text-slate-900">{pc}</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (mode === "contacts" && selectedLetter) {
    return (
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleBack} className="h-7 w-7">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <p className="text-sm font-bold text-slate-900">{selectedLetter}</p>
            <span className="text-xs text-slate-500">{filteredNames.length} contact{filteredNames.length !== 1 ? 's' : ''}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} className="h-7 w-7">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="max-h-[200px] overflow-y-auto space-y-1.5">
            {filteredNames.map((name) => (
              <button
                key={name}
                onClick={() => setSelectedName(name)}
                className="w-full p-2.5 rounded-lg border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-left transition-all flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs">
                    {name.charAt(0)}
                  </div>
                  <span className="font-medium text-slate-900">{name}</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </button>
            ))}
        </div>
      </div>
    );
  }

  // Main view with tabs
  return (
    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex rounded-lg bg-white border border-slate-200 p-0.5">
          <button
            onClick={() => setMode("contacts")}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              mode === "contacts" ? "bg-blue-500 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <BookUser className="w-3 h-3" />Contacts
          </button>
          <button
            onClick={() => setMode("manual")}
            data-testid="history-add-manual-tab"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              mode === "manual" ? "bg-blue-500 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Keyboard className="w-3 h-3" />Manual
          </button>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel} className="h-7 w-7">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {mode === "contacts" ? (
        !contacts || contacts.length === 0 ? (
          <div className="text-center py-4">
            <BookUser className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-xs text-slate-500">No contacts saved yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-9 gap-1">
            {fullAlphabet.map((letter) => {
              const hasContacts = alphabet.includes(letter);
              return (
                <button
                  key={letter}
                  onClick={() => hasContacts && setSelectedLetter(letter)}
                  disabled={!hasContacts}
                  data-testid={`history-contact-letter-${letter}`}
                  className={`aspect-square rounded text-xs font-bold flex items-center justify-center transition-all ${
                    hasContacts
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-slate-100 text-slate-300 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )
      ) : (
        <div className="flex items-center gap-2">
          <Input
            data-testid="history-add-stop-name"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="Name"
            className="text-sm h-8 flex-1 bg-white"
            onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
          />
          <Input
            data-testid="history-add-stop-postcode"
            value={manualPostcode}
            onChange={(e) => setManualPostcode(e.target.value)}
            placeholder="Postcode"
            className="text-sm h-8 w-28 bg-white"
            onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
          />
          <Button
            size="sm"
            data-testid="history-add-stop-confirm"
            onClick={handleManualSubmit}
            disabled={!manualName.trim() || !manualPostcode.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

export function HistoryTab({ history, onDeleteEntry, onUpdateNote, onExport, onAddStop, onRemoveStop, contacts, loading }) {
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [addingStopFor, setAddingStopFor] = useState(null);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return null;
    try {
      return new Date(isoStr).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return null;
    }
  };

  const getTimeSpan = (entry) => {
    const earliest = formatTime(entry.earliest_completed);
    const latest = formatTime(entry.latest_completed);
    if (!earliest || !latest) return null;
    if (earliest === latest) return earliest;
    return `${earliest} - ${latest}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p>No route history yet</p>
        <p className="text-sm mt-1">Completed routes will appear here when archived</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[500px] pr-4 custom-scrollbar">
      <div className="space-y-3">
        {history.map((entry) => {
          const timeSpan = getTimeSpan(entry);
          return (
            <div
              key={entry.id}
              data-testid={`history-entry-${entry.id}`}
              className="rounded-xl border border-slate-200 overflow-hidden bg-white"
            >
              <div
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setExpandedEntry(expandedEntry === entry.id ? null : entry.id);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">{formatDate(entry.date)}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{entry.stop_count} stop{entry.stop_count !== 1 ? "s" : ""}
                      </span>
                      {entry.notes_count > 0 && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />{entry.notes_count} note{entry.notes_count !== 1 ? "s" : ""}
                        </span>
                      )}
                      {timeSpan && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeSpan}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`export-history-${entry.id}`}
                    className="text-slate-400 hover:text-indigo-500 h-8 w-8"
                    title="Export route data"
                    onClick={(e) => { e.stopPropagation(); onExport(entry.id, entry.date); }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`delete-history-${entry.id}`}
                    className="text-slate-400 hover:text-red-500 h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); onDeleteEntry(entry.id); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedEntry === entry.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </div>

              <AnimatePresence>
                {expandedEntry === entry.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
                      {entry.stops.map((stop, idx) => (
                        <div key={stop.id || idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 group">
                          <div className="shrink-0 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">{stop.name}</p>
                            <p className="text-xs text-slate-500">{stop.postcode}</p>
                            <EditableNote
                              value={stop.notes}
                              onSave={(notes) => onUpdateNote(entry.id, idx, notes)}
                            />
                          </div>
                          <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`remove-history-stop-${idx}`}
                              onClick={() => onRemoveStop(entry.id, idx, stop.name, stop.postcode)}
                              className="text-slate-300 hover:text-red-500 hover:bg-red-50 h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove stop"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                      ))}

                      {/* Add stop via contact picker */}
                      {addingStopFor === entry.id ? (
                        <ContactPicker
                          contacts={contacts}
                          onAdd={(stop) => { onAddStop(entry.id, stop); setAddingStopFor(null); }}
                          onCancel={() => setAddingStopFor(null)}
                        />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`add-history-stop-${entry.id}`}
                          onClick={() => setAddingStopFor(entry.id)}
                          className="w-full text-xs text-slate-400 hover:text-blue-600 hover:bg-blue-50 gap-1.5 mt-1"
                        >
                          <Plus className="w-3.5 h-3.5" />Add Stop
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
